/**
 * M7 — "a política manda": uma única fonte visível para a exigência de check no app.
 *
 * A escala guarda `requiredCheckOurTeam` / `requiredCheckNotOurTeam`, duas flags que
 * decidem se o médico vê o botão de check-in no app e que NÃO aparecem em nenhuma tela.
 * A decisão do dono (mockup M7, blocos 2 a 5) rebaixa essas flags a **herança inicial**:
 *
 * - existe política vigente na data → a política decide, sozinha;
 * - não existe política vigente → valem as flags legadas da escala (transição);
 * - a primeira política de uma escala NASCE com o estado das flags, nunca desligada.
 *
 * Este módulo concentra as funções puras dessa regra — a herança, o resumo legível e a
 * linha "Check no app" do Detalhes do Plantão — para que as quatro telas que a exibem
 * (formulário da política, passo 3 do criar escala, Detalhes do Plantão e aba Requisitos)
 * digam exatamente a mesma coisa.
 */
import { CheckMethod, DSAttendanceMonitoringType } from '@/constants/ds-attendance.constants'
import type { DSAttendancePolicyData } from '@/types/ds-attendance.types'
import { parseLocalDate } from '@/utils/date'

/** Quem decidiu a exigência de check deste plantão. Campo novo do detalhe do plantão. */
export type RequiredCheckSource = 'POLICY' | 'SCHEDULE' | 'NONE'

/** Política que decidiu, como o backend a devolve no detalhe do plantão. */
export interface RequiredCheckPolicyRef {
  id: number | string
  name: string
  vigencyStart?: string | null
}

/** As duas flags legadas da escala, do jeito que o payload da escala as entrega. */
export interface ScheduleCheckFlags {
  ourTeam: boolean
  notOurTeam: boolean
}

/**
 * Estado inicial dos interruptores no passo 3 do CRIAR ESCALA.
 *
 * Confirmado no código, não presumido: a coluna `required_check_our_team` do backend
 * nasce `default: false` e a store do wizard (`useCreateScheduleStore.step3`) já nascia
 * com as duas flags em `false`. O M7 pede que a herança PRESERVE o default atual — e é
 * este. Mudar o default é decisão de produto, não deste componente.
 */
export const SCHEDULE_DEFAULT_CHECK_FLAGS: ScheduleCheckFlags = {
  ourTeam: false,
  notOurTeam: false
}

/** Método pré-selecionado quando o check nasce ligado (M7, balão ⑦). */
export const DEFAULT_CHECK_METHOD = CheckMethod.GEOLOCATION

/** Rótulos curtos do método, como a linha do plantão os escreve ("· geolocalização"). */
export const CHECK_METHOD_SHORT_LABELS: Record<CheckMethod, string> = {
  [CheckMethod.GEOLOCATION]: 'geolocalização',
  [CheckMethod.BIOMETRIC]: 'geolocalização com biometria',
  [CheckMethod.MANUAL]: 'manual'
}

/** `dd/mm/aaaa` sem escorregar de dia: datas puras (`YYYY-MM-DD`) são locais. */
export const formatVigencyDate = (value?: Date | string | null): string | null => {
  const date = parseLocalDate(value ?? null)
  if (!date) return null
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`
}

/** Só o dia importa na comparação de vigência — hora zerada dos dois lados. */
const atMidnight = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

/** O vínculo que vale na data: `startDate <= data` e (`endDate` vazio ou `>= data`). */
const isLinkVigent = (
  link: { startDate: Date | string | null; endDate: Date | string | null },
  reference: Date
): boolean => {
  const start = parseLocalDate(link.startDate)
  if (!start) return false
  const day = atMidnight(reference)
  if (atMidnight(start) > day) return false
  const end = parseLocalDate(link.endDate)
  return !end || atMidnight(end) >= day
}

/** A data de vigência que a tela cita: o vínculo vigente, senão o mais antigo. */
export const policyVigencyStart = (
  policy: DSAttendancePolicyData,
  reference: Date = new Date()
): Date | null => {
  const links = policy.links ?? []
  const vigent = links.find((link) => isLinkVigent(link, reference))
  const starts = links
    .map((link) => parseLocalDate(link.startDate))
    .filter((date): date is Date => !!date)
    .sort((a, b) => a.getTime() - b.getTime())
  return parseLocalDate(vigent?.startDate ?? null) ?? starts[0] ?? null
}

/**
 * A política que responde na data. `isActive === false` não responde por nada: uma
 * política desligada é exatamente o caso em que a escala volta a ser a fonte.
 */
export const findVigentPolicy = (
  policies: DSAttendancePolicyData[] | null | undefined,
  reference: Date = new Date()
): DSAttendancePolicyData | null =>
  (policies ?? []).find(
    (policy) =>
      policy.isActive !== false &&
      (policy.links ?? []).some((link) => isLinkVigent(link, reference))
  ) ?? null

/** A política só exige check quando a modalidade é o app — folha e app externo não têm botão. */
const policyRequiresCheck = (policy: DSAttendancePolicyData, ourTeam: boolean): boolean => {
  if (policy.monitoringType !== DSAttendanceMonitoringType.MINHA_ESCALA_APP) return false
  return Boolean(ourTeam ? policy.requiredCheckOurTeam : policy.requiredCheckNotOurTeam)
}

const policyCheckMethod = (policy: DSAttendancePolicyData, ourTeam: boolean): CheckMethod | null =>
  (ourTeam ? policy.checkMethodOurTeam : policy.checkMethodNotOurTeam) ?? null

/** O que a escala exige HOJE, por público — a linha de base dos avisos de desligamento. */
export interface CurrentCheckRequirement {
  source: RequiredCheckSource
  policy: RequiredCheckPolicyRef | null
  ourTeam: boolean
  notOurTeam: boolean
  methodOurTeam: CheckMethod | null
  methodNotOurTeam: CheckMethod | null
}

/**
 * `política ?? escala`, a mesma precedência que o backend já aplica (M7, bloco 2): a
 * política vigente responde em BLOCO; sem ela, respondem as flags legadas da escala.
 */
export const resolveCurrentCheckRequirement = (
  policies: DSAttendancePolicyData[] | null | undefined,
  scheduleFlags: ScheduleCheckFlags | null | undefined,
  reference: Date = new Date()
): CurrentCheckRequirement => {
  const policy = findVigentPolicy(policies, reference)

  if (policy) {
    return {
      source: 'POLICY',
      policy: {
        id: policy.id ?? '',
        name: policy.name,
        vigencyStart: formatVigencyDate(policyVigencyStart(policy, reference))
      },
      ourTeam: policyRequiresCheck(policy, true),
      notOurTeam: policyRequiresCheck(policy, false),
      methodOurTeam: policyCheckMethod(policy, true),
      methodNotOurTeam: policyCheckMethod(policy, false)
    }
  }

  const ourTeam = Boolean(scheduleFlags?.ourTeam)
  const notOurTeam = Boolean(scheduleFlags?.notOurTeam)

  return {
    source: ourTeam || notOurTeam ? 'SCHEDULE' : 'NONE',
    policy: null,
    ourTeam,
    notOurTeam,
    // A escala legada não guarda método: quem exige check por ela sempre exigiu geolocalização.
    methodOurTeam: ourTeam ? DEFAULT_CHECK_METHOD : null,
    methodNotOurTeam: notOurTeam ? DEFAULT_CHECK_METHOD : null
  }
}

/**
 * Resumo do bloco somente-leitura da aba Requisitos (M7, Tela 4). Sempre diz QUEM
 * decidiu: "pela política 'X'" ou "pela configuração da escala".
 */
export const buildScheduleCheckSummary = (current: CurrentCheckRequirement): string => {
  const audiences: string[] = []
  if (current.ourTeam) audiences.push('nosso time')
  if (current.notOurTeam) audiences.push('externos')

  if (!audiences.length) {
    return current.source === 'POLICY' && current.policy
      ? `check não exigido · política “${current.policy.name}”${vigencySuffix(current.policy)}.`
      : 'check não exigido nesta escala.'
  }

  const method = current.methodOurTeam ?? current.methodNotOurTeam
  const methodText = method ? ` · ${CHECK_METHOD_SHORT_LABELS[method]}` : ''
  const sourceText =
    current.source === 'POLICY' && current.policy
      ? ` · política “${current.policy.name}”${vigencySuffix(current.policy)}`
      : ' · configuração da escala (esta escala ainda não tem política de registro de plantão)'

  return `check exigido para ${audiences.join(' e ')}${methodText}${sourceText}.`
}

const vigencySuffix = (policy: RequiredCheckPolicyRef): string =>
  policy.vigencyStart ? ` (vigente desde ${policy.vigencyStart})` : ''

/** Entrada da linha "Check no app" do Detalhes do Plantão — campos ADITIVOS do backend. */
export interface StationCheckLineInput {
  requiredCheckSource?: RequiredCheckSource | null
  requiredCheckPolicy?: RequiredCheckPolicyRef | null
  /** Exigência já resolvida para ESTE plantão; ausente, cai no fallback documentado abaixo. */
  requiredCheck?: boolean | null
  requiredCheckMethod?: CheckMethod | null
  /** O plantão é do time interno? Decide qual flag legada responde. */
  ourTeam?: boolean | null
  scheduleFlags?: ScheduleCheckFlags | null
}

export interface StationCheckLine {
  required: boolean
  /** "exigido" / "não exigido" — o chip da linha. */
  statusLabel: string
  /** "geolocalização", quando o método é conhecido. */
  methodLabel: string | null
  /** "pela política “X” (vigente desde dd/mm/aaaa)" ou "pela configuração da escala …". */
  sourceLabel: string
  source: RequiredCheckSource
  /** "ver política" quando existe política; "criar política" na transição. */
  linkLabel: string
}

/**
 * A linha só existe quando o backend diz QUEM decidiu (§5, balão ⑮): "exigido" solto foi
 * exatamente o que deixou o incidente sem explicação. Enquanto `requiredCheckSource` não
 * vier, devolve `null` e a linha simplesmente não é renderizada — o Detalhes do Plantão
 * fica idêntico ao de hoje.
 */
export const buildStationCheckLine = (input: StationCheckLineInput): StationCheckLine | null => {
  const source = input.requiredCheckSource
  if (source !== 'POLICY' && source !== 'SCHEDULE' && source !== 'NONE') return null

  const required = resolveStationRequired(input, source)
  // Fonte "POLICY" sem a exigência resolvida: a tela não tem como saber o que a política
  // diz (o bloco dela não vem no detalhe do plantão) e inventar um "exigido" seria pior
  // que calar. A linha some até o backend expor o campo.
  if (required === null) return null

  return {
    required,
    statusLabel: required ? 'exigido' : 'não exigido',
    methodLabel:
      required && input.requiredCheckMethod
        ? CHECK_METHOD_SHORT_LABELS[input.requiredCheckMethod]
        : null,
    sourceLabel: buildStationSourceLabel(source, input.requiredCheckPolicy ?? null),
    source,
    linkLabel: source === 'POLICY' ? 'ver política' : 'criar política'
  }
}

const resolveStationRequired = (
  input: StationCheckLineInput,
  source: RequiredCheckSource
): boolean | null => {
  if (typeof input.requiredCheck === 'boolean') return input.requiredCheck
  if (source === 'NONE') return false
  if (source === 'SCHEDULE') {
    const flags = input.scheduleFlags
    if (!flags) return null
    return Boolean(input.ourTeam ? flags.ourTeam : flags.notOurTeam)
  }
  return null
}

const buildStationSourceLabel = (
  source: RequiredCheckSource,
  policy: RequiredCheckPolicyRef | null
): string => {
  if (source === 'POLICY' && policy) {
    return `pela política “${policy.name}”${vigencySuffix(policy)}`
  }
  if (source === 'POLICY') return 'pela política de registro de plantão vigente'
  if (source === 'SCHEDULE') {
    return 'pela configuração da escala — esta escala ainda não tem política de registro de plantão'
  }
  return 'não há política vigente nem exigência na configuração da escala'
}
